import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import Flex from "./Flex";
import Typo from "./Typo";

const Tooltip = ({
  children,
  label, // For plain tooltips
  rich, // Boolean to enable rich variant
  subhead, // Node: title for rich tooltip
  text, // Node: body for rich tooltip
  actions, // Node: buttons/links at the bottom
  position = "bottom",
  delay = 200,
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0, tx: "0%", ty: "0%" });

  const childRef = useRef(null);
  const timeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const calculateCoords = useCallback(() => {
    if (!childRef.current) return;
    const rect = childRef.current.getBoundingClientRect();
    const margin = 4; // Spacing from element

    let left = 0;
    let top = 0;
    let tx = "0%";
    let ty = "0%";

    switch (position) {
      case "top":
        left = rect.left + window.scrollX + rect.width / 2;
        top = rect.top + window.scrollY - margin;
        tx = "-50%";
        ty = "-100%";
        break;
      case "bottom":
        left = rect.left + window.scrollX + rect.width / 2;
        top = rect.bottom + window.scrollY + margin;
        tx = "-50%";
        ty = "0%";
        break;
      case "left":
        left = rect.left + window.scrollX - margin;
        top = rect.top + window.scrollY + rect.height / 2;
        tx = "-100%";
        ty = "-50%";
        break;
      case "right":
        left = rect.right + window.scrollX + margin;
        top = rect.top + window.scrollY + rect.height / 2;
        tx = "0%";
        ty = "-50%";
        break;
      default:
        break;
    }

    setCoords({ left, top, tx, ty });
  }, [position]);

  const showTooltip = () => {
    clearTimeout(hideTimeoutRef.current);
    clearTimeout(timeoutRef.current);
    if (!isRendered) {
      timeoutRef.current = setTimeout(() => {
        calculateCoords();
        setIsRendered(true);
        // Small timeout to allow render before animation class is added
        setTimeout(() => setIsVisible(true), 10);
      }, delay);
    } else {
      setIsVisible(true);
    }
  };

  const hideTooltip = () => {
    clearTimeout(timeoutRef.current);
    clearTimeout(hideTimeoutRef.current);

    // Set a short delay before hiding to allow mouse to bridge to the tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      // Wait for transition to finish before unmounting
      setTimeout(() => setIsRendered(false), 150);
    }, 200);
  };

  const forceHideTooltip = () => {
    clearTimeout(timeoutRef.current);
    clearTimeout(hideTimeoutRef.current);
    setIsVisible(false);
    setTimeout(() => setIsRendered(false), 150);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Close tooltip on scroll or resize
  useEffect(() => {
    if (isVisible) {
      window.addEventListener("scroll", forceHideTooltip, true);
      window.addEventListener("resize", forceHideTooltip);
      return () => {
        window.removeEventListener("scroll", forceHideTooltip, true);
        window.removeEventListener("resize", forceHideTooltip);
      };
    }
  }, [isVisible]);

  // If no label and no rich content is provided, just return the child
  if (!label && !rich && !subhead && !text) {
    return children;
  }

  const child = React.Children.only(children);
  const clonedChild = React.cloneElement(child, {
    ref: childRef,
    onMouseEnter: (e) => {
      showTooltip();
      if (child.props.onMouseEnter) child.props.onMouseEnter(e);
    },
    onMouseLeave: (e) => {
      hideTooltip();
      if (child.props.onMouseLeave) child.props.onMouseLeave(e);
    },
    onFocus: (e) => {
      showTooltip();
      if (child.props.onFocus) child.props.onFocus(e);
    },
    onBlur: (e) => {
      hideTooltip();
      if (child.props.onBlur) child.props.onBlur(e);
    },
  });

  const classes = ["ta-tooltip", className].filter(Boolean).join(" ");

  const tooltipPortal = true
    ? ReactDOM.createPortal(
        <div
          className={classes}
          style={{
            left: `${coords.left}px`,
            top: `${coords.top}px`,
            "--tx": coords.tx,
            "--ty": coords.ty,
          }}
          role="tooltip"
          data-position={position || undefined}
          data-visible={isVisible || undefined}
          data-rich={rich || undefined}
          onMouseEnter={rich ? showTooltip : undefined}
          onMouseLeave={rich ? hideTooltip : undefined}
        >
          {rich ? (
            <Flex spacing="1" vertical>
              {subhead && (
                <Typo
                  type="title"
                  size="s"
                  className="ta-color-text_on-surface"
                >
                  {subhead}
                </Typo>
              )}
              {text && (
                <Typo
                  type="body"
                  size="s"
                  className="ta-color-text_on-surface-variant"
                >
                  {text}
                </Typo>
              )}
              {actions && (
                <Flex spacing="2" className="ta-margin_top-xxs">
                  {typeof actions === "function"
                    ? actions({ close: forceHideTooltip })
                    : actions}
                </Flex>
              )}
            </Flex>
          ) : (
            label
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      {clonedChild}
      {tooltipPortal}
    </>
  );
};

export default Tooltip;
